/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback } from 'react'
// Third-party
import { CloseOutlined } from '@ant-design/icons'
import { css } from '@emotion/react'
import { Modal, App } from 'antd'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'

interface DeleteConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (force?: boolean) => Promise<void>
  title: string
  message: string
  itemName: string
  /** Whether to show force delete option (for divisions with children) */
  showForceOption?: boolean
  forceMessage?: string
}

const modalWrapperStyles = css`
  .ant-modal-content {
    background-color: ${colors.SECONDARY_300} !important;
    border: 1px solid ${colors.SECONDARY_100};
    border-radius: 8px;
    overflow: hidden;
  }

  .ant-modal-header {
    background-color: ${colors.SECONDARY_300} !important;
    border-bottom: 1px solid ${colors.SECONDARY_100};
    padding: 16px;
  }

  .ant-modal-body {
    background-color: ${colors.SECONDARY_300} !important;
    padding: 24px 16px;
  }

  .ant-modal-footer {
    background-color: ${colors.SECONDARY_300} !important;
    border-top: 1px solid ${colors.SECONDARY_100};
    padding: 0;
  }

  .ant-modal-close {
    color: ${colors.WHITE};
  }
`

const modalStyles = {
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    backdropFilter: 'blur(2px)',
  },
}

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  itemName,
  showForceOption = false,
  forceMessage,
}) => {
  const [loading, setLoading] = useState(false)
  const [showForceConfirm, setShowForceConfirm] = useState(false)
  const { message: antMessage } = App.useApp()

  const handleClose = useCallback(() => {
    setShowForceConfirm(false)
    onClose()
  }, [onClose])

  const handleConfirm = useCallback(
    async (force: boolean = false) => {
      setLoading(true)
      try {
        await onConfirm(force)
        antMessage.success(`"${itemName}" deleted successfully`)
        handleClose()
      } catch (err) {
        const errorMessage =
          (err as { message?: string })?.message || 'Failed to delete'

        // Check if error is about children (409 Conflict)
        if (
          showForceOption &&
          (errorMessage.toLowerCase().includes('children') ||
            errorMessage.toLowerCase().includes('child') ||
            (err as Error & { status?: number })?.status === 409)
        ) {
          setShowForceConfirm(true)
          setLoading(false)
          return
        }

        antMessage.error(errorMessage)
      } finally {
        setLoading(false)
      }
    },
    [onConfirm, itemName, antMessage, handleClose, showForceOption]
  )

  const closeIcon = (
    <CloseOutlined
      css={css`
        color: ${colors.SOLITUDE};
        font-size: 16px;
      `}
    />
  )

  const footer = (
    <div
      css={css`
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 16px;
        padding: 16px;
      `}
    >
      <Button
        text='Cancel'
        onClick={handleClose}
        variant='secondary'
        disabled={loading}
      />
      <Button
        text={showForceConfirm ? 'Force Delete' : 'Delete'}
        onClick={() => handleConfirm(showForceConfirm)}
        variant='danger'
        isLoading={loading}
        disabled={loading}
      />
    </div>
  )

  return (
    <Modal
      css={modalWrapperStyles}
      width={480}
      closeIcon={closeIcon}
      title={
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          {showForceConfirm ? 'Remove Children First' : title}
        </Text>
      }
      footer={footer}
      open={isOpen}
      onCancel={handleClose}
      centered
      styles={modalStyles}
      maskClosable={false}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        {showForceConfirm ? (
          <>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WARNING_200}
            >
              WARNING! This division has teams or child competitions.
            </Text>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {forceMessage ||
                'Deleting this division will also delete all teams and data associated with it. This action cannot be undone.'}
            </Text>
          </>
        ) : (
          <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
            <span
              css={css`
                color: ${colors.WARNING_200};
              `}
            >
              WARNING!
            </span>{' '}
            {message}
          </Text>
        )}
      </div>
    </Modal>
  )
}

export default DeleteConfirmModal
